import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import { getEmailTypeProps } from "../sendersUtil";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { deleteEmail, getEmails } from "../../../../api/EmailAPI";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import {
  Avatar,
  CloseButton,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import CreateEmailSender from "./CreateEmailSender";
import MainTable from "../../../../layouts/Table/MainTable";
import { IconCheck, IconChevronDown, IconX } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { getTypeIcon } from "../../workflows/CreateCampaign/flow/flowSettings";
import { poolsColumn } from "../../../../layouts/Table/columns/poolsColumn";
import EmailSendersActions from "./actions/EmailSendersActions";
import WarmupCell from "./warmup/WarmupCell";
import ReputationCell from "./ReputationCell";
import SelectionSharer from "../../../../components/TableComponents/SelectionSharer";
import { signatureColumn } from "../../../../layouts/Table/columns/signatureColumn";
import DomainsCell from "../../resellers/DomainsCell";
import FoldersDropdown from "../../lists/ListingWithContent/contacts/FoldersDropdown";
import {
  getRowClasses,
  minimizedSize,
} from "../../lists/ListingWithContent/listing";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import { useListStore } from "../../../../stores/ListStore";
import EmailEditPage from "./EmailEditPage";
import SelectionMover from "../../../../components/TableComponents/SelectionMover";
import ReconnectEmail from "./actions/ReconnectEmail";
import OvhDomain from "../ovh/OvhDomain";
import { sortByActiveId } from "../../../../functions/utilFunctions";
import PoolControl from "./PoolControl";

const EmailSendersListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const { poolId, accountId } = useParams();
  const account_id = useMemo(() => parseInt(accountId) || null, [accountId]);
  const [poolProfile, setPoolProfile] = useState(null);

  const columns = useMemo(() => {
    if (account_id) {
      return [
        nameColumn({
          disableLink: true,
          handleClick: () => {},
          editFunction:
            (row) =>
            ({ id, name }) =>
              getEmailTypeProps(row.account_type)?.editFunction({
                id,
                data: { name },
              }),
          width: 290,
          editorStyle: { right: 32 },
          apiRef,
          leftSection: function (row) {
            return (
              <Avatar
                color="initials"
                name={row.name}
                radius={"md"}
                size={36}
                variant="filled"
              />
            );
          },
        }),
        dateColumn(),
      ];
    } else
      return [
        numberColumn(),
        nameColumn({
          editFunction:
            (row) =>
            ({ id, name }) =>
              getEmailTypeProps(row.account_type)?.editFunction({
                id,
                data: { name },
              }),
          apiRef,
          width: 200,
          editorStyle: { right: 16 },
          handleClick: () => {},
          disableLink: true,
        }),
        nameColumn({
          editFunction:
            (row) =>
            ({ id, sender }) =>
              getEmailTypeProps(row.account_type)?.editFunction({
                id,
                data: { sender },
              }),
          headerName: lang.emails_account.columns.sender,
          width: 150,
          field: "sender",
          handleClick: () => {},
          disableLink: true,
          apiRef,
        }),
        {
          field: "email",
          headerName: lang.emails_account.columns.email,
          width: 175,
        },
        {
          field: "account_type",
          headerName: lang.emails_account.columns.type,
          width: 75,
          type: "options",
          options: [
            { label: "MassMailing", value: "MassMailing" },
            { label: "SMTP", value: "SMTP" },
            { label: "Gmail", value: "Gmail" },
            { label: "Outlook", value: "Outlook" },
          ],
          renderCell: ({ value }) => (
            <Group gap={"xs"} wrap="nowrap" w={"100%"}>
              {getTypeIcon(value, false, 22, {
                variant: "subtle",
                color: value === "SMTP" ? "primary" : "pink",
              })}
            </Group>
          ),
        },
        {
          field: "is_valid",
          headerName: lang.emails_account.columns.is_valid_column,
          width: 100,
          renderCell: ({ value, row }) => (
            <>
              <Tooltip
                label={
                  value
                    ? lang.emails_account.columns.is_valid
                    : lang.emails_account.columns.not_valid
                }
              >
                <ThemeIcon
                  variant="light"
                  color={value ? "tertiary.8" : "red"}
                  radius={"xl"}
                  size={"lg"}
                >
                  {value ? <IconCheck /> : <IconX />}
                </ThemeIcon>
              </Tooltip>
              {!value && (
                <ReconnectEmail current={row} setTableLoading={setLoading} />
              )}
            </>
          ),
        },
        signatureColumn({
          apiRef,
          editFunction:
            (row) =>
            ({ id, email_signatures_id }) =>
              getEmailTypeProps(row.account_type)?.editFunction({
                id,
                data: { email_signatures_id, connection_test: false },
              }),
        }),
        poolsColumn({
          apiRef,
          editFunction:
            (row) =>
            ({ id, pools_ids }) =>
              getEmailTypeProps(row.account_type)?.editFunction({
                id,
                data: { pools_ids },
              }),
          type: "Email",
        }),
        {
          field: "warmup_settings",
          headerName: "Warm-Up",
          width: 120,
          renderCell: ({ value, row }) =>
            row.account_type === "SMTP" ? (
              <WarmupCell settings={value} row={row} apiRef={apiRef} />
            ) : (
              "-"
            ),
        },
        {
          field: "reputation",
          headerName: "Reputation",
          width: 90,
          renderCell: ({ value, row }) =>
            row.account_type === "SMTP" ? (
              <ReputationCell value={value} />
            ) : (
              "-"
            ),
        },
        {
          field: "custom_tracking_domain",
          headerName: lang.emails_account.columns.custom_domain,
          width: 180,
          renderCell: ({ row, value }) => (
            <DomainsCell
              status={row.custom_tracking_domain_status}
              value={value}
              preferEmpty
            />
          ),
        },
        dateColumn({
          field: "last_use",
          headerName: lang.emails_account.columns.last_use,
        }),
        dateColumn(),
        sharingColumn({
          apiRef,
          editFunction: (row) =>
            getEmailTypeProps(row.account_type)?.editFunction,
        }),
      ];
  }, [account_id, apiRef, lang]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const handleRowClick = ({ row }) => {
    const setMinimized = useListStore.getState().setModelMinimized;

    if (poolId) {
      if (account_id === row.id) {
        navigate(`/senders/pool/${poolId}/email/none`);
      } else {
        navigate(`/senders/pool/${poolId}/email/${row.id}`);
        setMinimized(true);
      }
    } else {
      if (account_id === row.id) {
        navigate(`/senders/email/none`);
      } else {
        navigate(`/senders/email/${row.id}`);
        setMinimized(true);
      }
    }
  };

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <EmailSendersActions
            current={row}
            onFinish={() => setLoading(true)}
          />

          <IconChevronDown
            size={24}
            className="bump"
            opacity={account_id === row.id ? 1 : 0.4}
            style={{
              transform: account_id === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [account_id]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteEmail}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          rows={rows}
        />
        <SelectionMover
          rows={rows}
          getEditFunction={(row) =>
            ({ id, pools_ids }) =>
              getEmailTypeProps(row.account_type)?.editFunction({
                id,
                data: { pools_ids, connection_test: false },
              })}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          folderType={"Email"}
          isPool
        />
        <SelectionSharer
          getEditFunction={(row) =>
            getEmailTypeProps(row.account_type)?.editFunction
          }
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          rows={rows}
        />
        {/* <ReconnectAll setTableLoading={setLoading} ids={selectedIds} /> */}
      </>
    ),
    [apiRef, rows]
  );

  const navigate = useNavigate();

  const handleClose = () => {
    if (poolId) {
      navigate(`/senders/pool/${poolId}/email/none?highlight=${accountId}`);
    } else {
      navigate(`/senders/email/none?highlight=${accountId}`);
    }
  };

  useEffect(() => {
    if (account_id) {
      apiRef.current.setColumnVisibility("created_on", false);
    } else {
      apiRef.current.setColumnVisibility("created_on", true);
    }
  }, [apiRef, account_id]);

  useEffect(() => {
    if (poolId !== "none") setLoading(true);
  }, [poolId]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getEmails,
    responseField: "email_accounts_list",
    setRows,
    folderId: poolId,
    isPool: true,
    setPoolProfile,
  });

  return (
    <Stack flex={1} px={48} pt={16} gap={0} h={"calc(100vh - 70px)"}>
      <Group gap={"xl"}>
        <Text size="xl" fw={600} mt={16} mb={8} mr={"auto"}>
          {lang.new_senders.email_title}
        </Text>
        <OvhDomain onFinish={() => setLoading(true)} />
        <CreateEmailSender setTableLoading={setLoading} />
      </Group>

      {poolId && (
        <Group mt={8}>
          <Group w={382}>
            <FoldersDropdown
              type="Email"
              getClickLink={(id) => `/senders/pool/${id}/email/none`}
              getDeletedLink={() => `/senders/email/none`}
              isPool={true}
            />
          </Group>

          <PoolControl pool={poolProfile} />
        </Group>
      )}
      <Divider my={16} />
      <Flex flex={1}>
        <Group
          flex={1}
          wrap="nowrap"
          align="stretch"
          gap={0}
          style={{ overflowX: "hidden" }}
          pt={64}
          mt={-64}
        >
          <Flex
            flex={1}
            maw={account_id ? minimizedSize - 48 : null}
            pos={"relative"}
          >
            <NoItemsModal
              rows={rows}
              loading={loading}
              text={
                poolId ? (
                  <Text size="xl" mb={32}>
                    {lang.new_senders.no_email_in_pool}
                  </Text>
                ) : (
                  <Text size="xl" mb={32}>
                    {lang.new_senders.no_email}
                  </Text>
                )
              }
              button={
                <CreateEmailSender
                  setTableLoading={setLoading}
                  inPool={!!poolId}
                />
              }
            />
            <MainTable
              apiRef={apiRef}
              columns={columns}
              rows={sortByActiveId(rows, account_id)}
              loading={loading}
              setLoading={setLoading}
              id={"model-table"}
              toolbarActions={account_id ? null : toolbarActions}
              actionsCell={actionsCell}
              onRowClick={handleRowClick}
              hasFolder
              pageName={"integrations_email"}
              pageOrderName={"integrations_email_order"}
              whiteButtons={!account_id}
              actionsCellWidth={75}
              disableGotoPage={account_id}
              disableVisibleColumns={account_id}
              defaultPerPage={15}
              // disablePerPage={account_id}
              disableCheckboxes={!!account_id}
              getRowClassName={(params) =>
                getRowClasses({
                  params,
                  mainId: accountId,
                  integerId: account_id,
                })
              }
              withSorter={!!account_id}
              tableSx={{
                "& .MuiDataGrid-columnHeaders": {
                  border: "none !important",
                  display: account_id ? "none" : "flex",
                },
                "& .MuiDataGrid-cell": {
                  border: "none",
                },
                "& .MuiDataGrid-main": {
                  background: "var(--mantine-color-primary-2)",
                  border: "none !important",
                  borderRadius: 3,
                },
                "& .table-search": {
                  width: account_id ? "200px !important" : "300px !important",
                },
                "& .MuiDataGrid-footerContainer": {
                  border: "none !important",
                },
                "& .table-row": {
                  cursor: "pointer",
                  borderRadius: 3,
                  marginBottom: 1,
                  background: "#fff",
                  border: "1px solid var(--mantine-color-primary-1)",
                },
                "& .MuiDataGrid-pinnedColumnHeaders": {
                  background: "transparent",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right .table-row": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderLeft: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left .table-row": {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
                  borderRadius: account_id ? 3 : 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                "& .MuiDataGrid-selectedRowCount": {
                  visibility: account_id ? "hidden" : "visible",
                },
                "& .MuiPagination-root": {
                  left: account_id ? 0 : undefined,
                  transform: account_id ? "none" : undefined,
                },
                "& .MuiTablePagination-input": {
                  position: "relative",
                  zIndex: 1
                }
              }}
            />
          </Flex>

          <Stack
            flex={1}
            maw={account_id ? "none" : 0}
            pl={32}
            style={{ transition: "all ease .3s", overflow: "hidden" }}
            h={"auto"}
            pos={"relative"}
          >
            <CloseButton
              style={{
                position: "absolute",
                left: 48,
                top: 8,
                zIndex: 4,
              }}
              size={40}
              onClick={handleClose}
            />
            {account_id && !loading && (
              <EmailEditPage
                handleClose={handleClose}
                onEdited={() => {
                  setLoading(true);
                }}
              />
            )}
          </Stack>
        </Group>
      </Flex>
    </Stack>
  );
};

export default EmailSendersListing;
